<script setup lang="ts">
import { computed } from 'vue';
import { useTrip } from '@/stores/trip';
import { BANDS } from '@/core/constants';

/**
 * Légende contextuelle de la carte.
 *
 * Deux blocs :
 *   1. Bandes temporelles (toujours visible) — couleurs des isochrones par durée.
 *   2. Distinction train de nuit / bus de nuit — visible UNIQUEMENT en mode 'night',
 *      pour clarifier que tous les trajets "nuit" ne se valent pas (cf. Q2 du tri :
 *      "Distinguer dans l'UI : icône différente pour bus de nuit dans la légende").
 *
 * On calcule dynamiquement les bandes à afficher (toutes celles ≤ maxHours, plus
 * la première au-delà pour donner du contexte sur la coupure).
 */
const trip = useTrip();

const visibleBands = computed(() => {
  const max = trip.state.maxHours;
  let lastShown = -1;
  for (let i = 0; i < BANDS.length; i++) {
    if (BANDS[i].max <= max) lastShown = i;
    else if (lastShown >= 0) {
      lastShown = i;
      break;
    } else {
      lastShown = i;
      break;
    }
  }
  return BANDS.slice(0, lastShown + 1);
});

const isNightMode = computed(() => trip.state.routeMode === 'night');
</script>

<template>
  <div class="space-y-3">
    <!-- Bandes temporelles -->
    <div>
      <div class="text-xs font-medium text-text-muted uppercase tracking-wider mb-2">
        Temps de trajet
      </div>
      <div class="flex flex-wrap gap-x-3 gap-y-1.5 text-xs text-text-muted">
        <span
          v-for="b in visibleBands"
          :key="b.color"
          class="inline-flex items-center gap-1.5"
        >
          <span
            class="inline-block w-3 h-3 rounded-full border border-DEFAULT"
            :style="{ background: b.color }"
          ></span>
          {{ b.label }}
        </span>
      </div>
    </div>

    <!-- Distinction train de nuit / bus de nuit (mode nuit uniquement) -->
    <div v-if="isNightMode" class="pt-2 border-t border-DEFAULT">
      <div class="text-xs font-medium text-text-muted uppercase tracking-wider mb-2">
        Mode nuit
      </div>
      <div class="space-y-1.5 text-xs text-text-muted">
        <span class="inline-flex items-center gap-2">
          <i class="ti ti-moon text-mode-night" aria-hidden="true"></i>
          <span><strong class="text-text">Train de nuit</strong> — privilégié (couchette ≥ 6h)</span>
        </span>
        <br />
        <span class="inline-flex items-center gap-2">
          <i class="ti ti-bus text-mode-bus" aria-hidden="true"></i>
          <span><strong class="text-text">Bus de nuit</strong> — affiché mais non privilégié</span>
        </span>
      </div>
      <p class="text-[11px] text-text-faint mt-2 leading-relaxed">
        Seules les villes accessibles via au moins un <strong>train de nuit ≥ 6h</strong>
        sont affichées (Nightjet, European Sleeper, CFR, etc.). Les correspondances
        de jour sont autorisées pour rejoindre / quitter le train de nuit.
      </p>
    </div>
  </div>
</template>
